import { useEffect, useState } from "react";
import { Download, RotateCcw, Search } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { DashboardShell } from "../../components/DashboardShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const STATUSES = ["awaiting_intake", "questionnaires_pending", "ready_to_book", "interview_scheduled",
  "report_in_progress", "qa_review", "returned", "delivered"];

export default function AdminBI() {
  const [tab, setTab] = useState("dashboard");
  const [clinicians, setClinicians] = useState([]);
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState({ from_date: "", to_date: "", clinician_id: "", product: "", status: "" });
  const [includeTest, setIncludeTest] = useState(false);
  const [data, setData] = useState(null);
  const [logs, setLogs] = useState(null);
  const [logAction, setLogAction] = useState("");

  useEffect(() => {
    api.get("/admin/users").then((r) => setClinicians(r.data.filter((u) => u.role === "clinician"))).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const run = async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      if (includeTest) params.include_test = "true";
      const { data } = await api.get("/admin/bi", { params });
      setData(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { run(); }, [includeTest]);

  const loadLogs = () => {
    api.get("/admin/audit-log", { params: logAction ? { action: logAction } : {} })
      .then((r) => setLogs(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { if (tab === "audit") loadLogs(); }, [tab, logAction]);

  const exportUrl = (fmt) => {
    const params = new URLSearchParams({ format: fmt, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) });
    if (includeTest) params.set("include_test", "true");
    return `${process.env.REACT_APP_BACKEND_URL}/api/admin/bi/export?${params}`;
  };

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v === "__all__" ? "" : v }));
  const s = data?.by_status || {};

  return (
    <DashboardShell title="Business intelligence" subtitle="Operational reporting across evaluations, providers, and QA — filter, analyze, export.">
      <div className="flex gap-2 mb-8">
        {[["dashboard", "Reports"], ["audit", "Audit log"]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`bi-tab-${v}`}
            className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${tab === v ? "bg-[#1E3F20] text-white" : "bg-[#E3DAC9]/50 text-[#5C5C57] hover:bg-[#E3DAC9]"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "dashboard" && (
        <>
          <div className="card-warm p-6 mb-8 grid grid-cols-2 md:grid-cols-6 gap-4 items-end" data-testid="bi-filters">
            <div className="space-y-1.5"><p className="text-[11px] uppercase tracking-wide font-bold text-[#5C5C57]">From</p>
              <Input type="date" value={filters.from_date} onChange={(e) => set("from_date", e.target.value)} data-testid="bi-from" className="input-warm border-[#E5E5DF] h-9 text-sm" /></div>
            <div className="space-y-1.5"><p className="text-[11px] uppercase tracking-wide font-bold text-[#5C5C57]">To</p>
              <Input type="date" value={filters.to_date} onChange={(e) => set("to_date", e.target.value)} data-testid="bi-to" className="input-warm border-[#E5E5DF] h-9 text-sm" /></div>
            <div className="space-y-1.5"><p className="text-[11px] uppercase tracking-wide font-bold text-[#5C5C57]">Provider</p>
              <Select value={filters.clinician_id || "__all__"} onValueChange={(v) => set("clinician_id", v)}>
                <SelectTrigger data-testid="bi-clinician" className="border-[#E5E5DF] h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="__all__">All providers</SelectItem>
                  {clinicians.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><p className="text-[11px] uppercase tracking-wide font-bold text-[#5C5C57]">Evaluation type</p>
              <Select value={filters.product || "__all__"} onValueChange={(v) => set("product", v)}>
                <SelectTrigger data-testid="bi-product" className="border-[#E5E5DF] h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="__all__">All types</SelectItem>
                  {products.map((p) => <SelectItem key={p.slug} value={p.slug}>{p.short}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><p className="text-[11px] uppercase tracking-wide font-bold text-[#5C5C57]">Status</p>
              <Select value={filters.status || "__all__"} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="bi-status" className="border-[#E5E5DF] h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="__all__">All statuses</SelectItem>
                  {STATUSES.map((st) => <SelectItem key={st} value={st}>{st.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="flex gap-2 items-center">
              <Button onClick={run} data-testid="bi-search-btn" className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white h-9"><Search className="h-4 w-4" /></Button>
              <Button variant="outline" data-testid="bi-reset-btn" className="rounded-full border-[#E5E5DF] h-9"
                onClick={() => { setFilters({ from_date: "", to_date: "", clinician_id: "", product: "", status: "" }); setTimeout(run, 0); }}>
                <RotateCcw className="h-4 w-4" /></Button>
            </div>
            <label className="flex items-center gap-2 text-sm col-span-2" data-testid="bi-include-test-label"
              title="Standard/Test cards always show all cases in range; this toggle affects the metrics and cases table.">
              <input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)}
                data-testid="bi-include-test" className="h-4 w-4 accent-[#1E3F20]" />
              Include test clients <span className="text-xs text-[#5C5C57]">(excluded from metrics by default; Standard/Test cards always show all cases in range)</span>
            </label>
          </div>

          {data && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8" data-testid="bi-summary">
                {[["Total evaluations", data.total], ["Standard", data.standard_count], ["Test", data.test_count],
                  ["Delivered", s.delivered || 0], ["In progress", (s.report_in_progress || 0) + (s.interview_scheduled || 0) + (s.questionnaires_pending || 0)],
                  ["Pending QA", s.qa_review || 0], ["Revenue", `$${data.revenue.toLocaleString()}`]].map(([k, v]) => (
                  <div key={k} className="card-warm p-5" data-testid={`bi-stat-${k.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                    <p className="text-[11px] tracking-[0.15em] uppercase font-bold text-[#5C5C57]">{k}</p>
                    <p className="font-heading text-3xl font-semibold mt-1">{v}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <div className="card-warm overflow-hidden" data-testid="bi-by-provider">
                  <p className="px-6 py-4 font-heading text-lg font-semibold border-b border-[#E5E5DF]">Evaluations by provider</p>
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs tracking-[0.15em] uppercase text-[#5C5C57] border-b border-[#E5E5DF]">
                      <th className="px-6 py-3">Provider</th><th className="px-6 py-3">Total</th><th className="px-6 py-3">Delivered</th>
                      <th className="px-6 py-3">In progress</th><th className="px-6 py-3">Avg days</th></tr></thead>
                    <tbody>
                      {data.by_provider.map((p) => (
                        <tr key={p.clinician} className="border-b border-[#E5E5DF] last:border-0" data-testid={`bi-provider-${p.clinician.replace(/\s/g, "-")}`}>
                          <td className="px-6 py-3 font-semibold">{p.clinician}</td><td className="px-6 py-3">{p.total}</td>
                          <td className="px-6 py-3">{p.delivered}</td><td className="px-6 py-3">{p.in_progress}</td>
                          <td className="px-6 py-3">{p.avg_completion_days ?? "—"}</td>
                        </tr>
                      ))}
                      {data.by_provider.length === 0 && <tr><td colSpan={5} className="px-6 py-6 text-center text-[#5C5C57]">No data for these filters.</td></tr>}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-8">
                  <div className="card-warm overflow-hidden" data-testid="bi-by-type">
                    <p className="px-6 py-4 font-heading text-lg font-semibold border-b border-[#E5E5DF]">Evaluations by type</p>
                    <table className="w-full text-sm">
                      <tbody>
                        {data.by_product.map((p) => (
                          <tr key={p.product} className="border-b border-[#E5E5DF] last:border-0">
                            <td className="px-6 py-3 font-semibold">{p.product}</td>
                            <td className="px-6 py-3 text-right">{p.count}</td>
                            <td className="px-6 py-3 text-right text-[#5C5C57]">{p.pct}%</td>
                          </tr>
                        ))}
                        {data.by_product.length === 0 && <tr><td className="px-6 py-6 text-center text-[#5C5C57]">No data.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  <div className="card-warm overflow-hidden" data-testid="bi-by-month">
                    <p className="px-6 py-4 font-heading text-lg font-semibold border-b border-[#E5E5DF]">Evaluations by month</p>
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-xs tracking-[0.15em] uppercase text-[#5C5C57] border-b border-[#E5E5DF]">
                        <th className="px-6 py-3">Month</th><th className="px-6 py-3">Initiated</th><th className="px-6 py-3">Delivered</th></tr></thead>
                      <tbody>
                        {data.by_month.map((m) => (
                          <tr key={m.month} className="border-b border-[#E5E5DF] last:border-0">
                            <td className="px-6 py-3 font-semibold">{m.month}</td>
                            <td className="px-6 py-3">{m.initiated}</td><td className="px-6 py-3">{m.delivered}</td>
                          </tr>
                        ))}
                        {data.by_month.length === 0 && <tr><td colSpan={3} className="px-6 py-6 text-center text-[#5C5C57]">No data.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="card-warm overflow-hidden" data-testid="bi-cases-table">
                <div className="px-6 py-4 flex items-center justify-between border-b border-[#E5E5DF]">
                  <p className="font-heading text-lg font-semibold">Evaluations ({data.cases.length}{data.total > 500 ? ` of ${data.total}` : ""})</p>
                  <div className="flex gap-2">
                    <a href={exportUrl("csv")} data-testid="bi-export-csv"><Button variant="outline" size="sm" className="rounded-full border-[#1E3F20]/30 text-[#1E3F20]"><Download className="h-3.5 w-3.5 mr-1.5" /> CSV</Button></a>
                    <a href={exportUrl("xlsx")} data-testid="bi-export-xlsx"><Button variant="outline" size="sm" className="rounded-full border-[#1E3F20]/30 text-[#1E3F20]"><Download className="h-3.5 w-3.5 mr-1.5" /> Excel</Button></a>
                  </div>
                </div>
                <div className="max-h-[420px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white"><tr className="text-left text-xs tracking-[0.15em] uppercase text-[#5C5C57] border-b border-[#E5E5DF]">
                      <th className="px-6 py-3">Client</th><th className="px-6 py-3">Evaluation</th><th className="px-6 py-3">Clinician</th>
                      <th className="px-6 py-3">Status</th><th className="px-6 py-3">Created</th></tr></thead>
                    <tbody>
                      {data.cases.map((c) => (
                        <tr key={c.case_id} className="border-b border-[#E5E5DF] last:border-0">
                          <td className="px-6 py-2.5 font-semibold">{c.client}</td><td className="px-6 py-2.5 text-[#5C5C57]">{c.product}</td>
                          <td className="px-6 py-2.5 text-[#5C5C57]">{c.clinician}</td><td className="px-6 py-2.5">{c.status.replace(/_/g, " ")}</td>
                          <td className="px-6 py-2.5 text-[#5C5C57]">{new Date(c.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {tab === "audit" && (
        <div className="card-warm overflow-hidden" data-testid="audit-log-table">
          <div className="px-6 py-4 border-b border-[#E5E5DF] flex items-center gap-3">
            <p className="font-heading text-lg font-semibold">Audit log</p>
            <Select value={logAction || "__all__"} onValueChange={(v) => setLogAction(v === "__all__" ? "" : v)}>
              <SelectTrigger data-testid="audit-action-filter" className="border-[#E5E5DF] h-8 text-xs w-56"><SelectValue placeholder="All actions" /></SelectTrigger>
              <SelectContent><SelectItem value="__all__">All actions</SelectItem>
                {["login", "register", "profile_change", "message_sent", "appointment_created", "calendar_event_created",
                  "report_downloaded", "qa_review", "bi_export"].map((a) => <SelectItem key={a} value={a}>{a.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="max-h-[560px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white"><tr className="text-left text-xs tracking-[0.15em] uppercase text-[#5C5C57] border-b border-[#E5E5DF]">
                <th className="px-6 py-3">Time</th><th className="px-6 py-3">User</th><th className="px-6 py-3">Role</th>
                <th className="px-6 py-3">Action</th><th className="px-6 py-3">Object</th><th className="px-6 py-3 hidden lg:table-cell">Details</th></tr></thead>
              <tbody>
                {(logs || []).map((l) => (
                  <tr key={l.id} className="border-b border-[#E5E5DF] last:border-0" data-testid={`audit-row-${l.id}`}>
                    <td className="px-6 py-2.5 text-[#5C5C57] whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="px-6 py-2.5 font-semibold">{l.user_name || "—"}</td>
                    <td className="px-6 py-2.5 capitalize">{l.role || "—"}</td>
                    <td className="px-6 py-2.5"><span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#E3DAC9]/60">{l.action}</span></td>
                    <td className="px-6 py-2.5 text-[#5C5C57]">{l.object_type} {l.object_id ? `#${l.object_id.slice(-6)}` : ""}</td>
                    <td className="px-6 py-2.5 text-xs text-[#5C5C57] hidden lg:table-cell max-w-xs truncate">{JSON.stringify(l.details)}</td>
                  </tr>
                ))}
                {logs && logs.length === 0 && <tr><td colSpan={6} className="px-6 py-8 text-center text-[#5C5C57]">No audit events yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}
